import { type ComponentProps, type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { standardizePhoneNumber } from '../../utils';
import type { LoginScreenProps, PhoneNumberFormInput } from './login-form.types';
import { PhoneField } from '..';
import { Button, getColorVar, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { AuthenticationFaqNavigator } from '../authentication-faq';
import { checkoutContactSchema } from './login-form.schema';
import { useAuthProvider } from '../../providers';
import { useAuth } from '../../hooks';
import { authStore$ } from '../../stores/auth.store';

import styles from './login-form.module.css';

/**
 * LoginForm — Unified phone input form.
 *
 * Single entry point for both login and register flows.
 * User enters phone number → system auto-detects in SelectChannel step
 * whether to use Cognito (login) or Magento (register) OTP.
 *
 * No login/register toggle needed — the flow is fully automatic.
 * Phone number is preserved in the store so it persists across step transitions.
 */
export const LoginForm: FC<LoginScreenProps> = ({ FormComponent }) => {
    const { t } = useTranslation('auth');
    const { initValidatePhone } = useAuthProvider();
    const { setCurrentStep } = useAuth();

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Read stored phone number so it persists when user navigates back from errors
    const storedPhoneNumber = authStore$.initValidatePhoneData.get()?.phoneNumber ?? '';

    const formDefaultValues = useMemo(() => ({ phone_number: storedPhoneNumber }), [storedPhoneNumber]);

    const loadingStyle: ComponentProps<typeof Button>['loadingProps'] = useMemo(() => {
        return {
            color: getColorVar('primary', '0'),
        };
    }, []);

    const onSubmit = useCallback(
        async (data: PhoneNumberFormInput) => {
            setIsSubmitting(true);

            initValidatePhone({
                phoneNumber: data.phone_number,
            })
                .then(() => {
                    setIsSubmitting(false);
                    setCurrentStep('SelectChannel');
                })
                .catch(e => {
                    setIsSubmitting(false);
                    console.error('e', e);
                });
        },
        [initValidatePhone, setCurrentStep]
    );

    const handleSubmit = useCallback(
        (data: PhoneNumberFormInput) => {
            const phoneNumber = standardizePhoneNumber(`+84${data.phone_number}`);
            const validPhone = '0' + phoneNumber.slice(3);
            onSubmit({
                phone_number: validPhone,
            });
        },
        [onSubmit]
    );

    return (
        <FormComponent<PhoneNumberFormInput>
            onSubmit={handleSubmit}
            validationSchema={checkoutContactSchema}
            className={styles.phoneNumberForm}
            useFormProps={{ defaultValues: formDefaultValues }}
        >
            {({ control, formState: { errors }, resetField }) => (
                <div className={styles.phoneNumberFormContainer}>
                    <div className={styles.phoneNumberFormWrapper}>
                        <div className={styles.containerPhone}>
                            <PhoneField
                                name="phone_number"
                                placeholder={t('placeholder_phone_field')}
                                control={control}
                                errorMessage={errors?.phone_number?.message}
                                onResetValue={() => {
                                    resetField('phone_number');
                                }}
                            />
                        </div>

                        <AuthenticationFaqNavigator />

                        <div className={styles.footer}>
                            <Button
                                type="submit"
                                aria-label="Submit phone number"
                                disabled={isSubmitting}
                                loading={isSubmitting}
                                className={styles.submitButton}
                                loadingProps={loadingStyle}
                            >
                                <Text className={styles.textButton}>{t('text_continue')}</Text>
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </FormComponent>
    );
};
